import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { uploadModeratedMedia, mergeModerationTokens, ModerationTokenMap } from '../utils/moderatedUpload';
import { describeModerationError } from '../utils/moderationErrors';
import { checkUploadSize } from '../utils/mediaApi';
import TopNavigation from '../components/TopNavigation';
import VerifiedBadge from '../components/VerifiedBadge';
import ImageWithFallback from '../components/ImageWithFallback';
import { SelectedMediaThumb } from '../components/PostMediaGallery';
import { extractHashtags } from '../utils/text';
import { validateContent } from '../utils/contentModeration';
import { createPost, followPage, getPageById, updatePage } from '../utils/secureFunctions';

interface PageDoc {
  id: string;
  name: string;
  username: string;
  description: string;
  category: string;
  photoURL: string;
  coverPhotoURL: string;
  website?: string;
  email?: string;
  phone?: string;
  location?: string;
  ownerId: string;
  admins: string[];
  editors: string[];
  followers: string[];
}

const PageDetail: React.FC = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<PageDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', category: '' });
  const uid = auth.currentUser?.uid || '';
  // Create post as page
  const [postText, setPostText] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [posting, setPosting] = useState(false);
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [moderating, setModerating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!pageId) return;
      setLoading(true);
      try {
        const res = await getPageById(pageId);
        if (res.page) {
          const data = res.page as any;
          setPage(data as PageDoc);
          setForm({
            name: data.name || '',
            description: data.description || '',
            category: data.category || ''
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pageId]);

  const isOwner = page && page.ownerId === uid;
  const isAdmin = page && page.admins?.includes(uid);
  const canManage = !!(isOwner || isAdmin);
  const isFollowing = !!(page && page.followers?.includes(uid));

  const handleFollowToggle = async () => {
    if (!auth.currentUser || !page) return;
    try {
      const res = await followPage(page.id, !isFollowing);
      setPage(res.page as PageDoc);
    } catch (e) {
      console.error('Follow toggle failed', e);
      alert('Failed to update follow status');
    }
  };

  const handleSave = async () => {
    if (!page) return;
    try {
      const res = await updatePage(page.id, {
        name: form.name,
        description: form.description,
        category: form.category,
      });
      setPage(res.page as PageDoc);
      setEditing(false);
    } catch (e) {
      console.error('Update failed', e);
      alert('Failed to update page');
    }
  };

  const handleCreatePagePost = async () => {
    if (!auth.currentUser || !page) return;
    if (!postText.trim() && !mediaFile) return;
    // Basic text validation for parity with Home
    if (postText.trim()) {
      const validation = validateContent(postText);
      if (!validation.isValid) {
        alert(validation.message || 'Invalid content');
        return;
      }
    }
    setPosting(true);
    try {
      let imageURL = '';
      let videoURL = '';
      let mediaPath = '';
      let moderationTokens: ModerationTokenMap | undefined;

      if (mediaFile) {
        setUploadingMedia(true);
        setCompressionProgress(0);
        setModerating(true);
        try {
          const field = mediaFile.type.startsWith('video') ? 'videoURL' : 'imageURL';
          const upload = await uploadModeratedMedia({
            folder: field === 'videoURL' ? 'postVideos' : 'postImages',
            field,
            file: mediaFile,
            metadata: {
              caption: postText,
              pageId: page.id,
              pageName: page.name,
            },
            onProgress: (pct) => setCompressionProgress(pct),
          });
          moderationTokens = mergeModerationTokens(moderationTokens, upload);
          mediaPath = upload.storagePath;
          if (field === 'imageURL') {
              imageURL = upload.objectKey;
          } else {
              videoURL = upload.objectKey;
          }
        } finally {
          setUploadingMedia(false);
          setCompressionProgress(0);
          setModerating(false);
        }
      }

      const moderationStatus: 'approved' | 'pending' = 'approved';
      const moderationReason: string | null = null;

      // Extract hashtags (minimal)
      const hashtags: string[] = extractHashtags ? extractHashtags(postText) : [];

      await createPost({
        // Creator (for permissions/audit) and display as page
        userId: auth.currentUser.uid,
        userName: page.name,
        userPhoto: page.photoURL,
        // Page ownership/display metadata
        ownerType: 'page',
        pageId: page.id,
        pageName: page.name,
        pagePhoto: page.photoURL,

        content: postText,
        imageURL,
        videoURL,
        mediaPath,
        moderationTokens: moderationTokens || null,
        moderation: { status: mediaFile ? moderationStatus : 'approved', reason: mediaFile ? moderationReason : null, scores: {} },
        hashtags,
      } as any);

      setPostText('');
      setMediaFile(null);
      if (fileRef.current) fileRef.current.value = '';
      alert('Post published to your followers');
    } catch (e) {
      console.error('Failed to create page post', e);
      const friendly = describeModerationError(e);
      alert(friendly || 'Failed to create page post');
    } finally {
      setPosting(false);
    }
  };
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (!checkUploadSize(e.target.files[0])) { e.target.value = ''; return; }
      // Videos upload as-is; the server re-compresses them at their original resolution after moderation.
      setMediaFile(e.target.files[0]);
    }
  };

  const handleRemoveSelectedMedia = () => {
    setMediaFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  if (loading || !page) {
    return (
      <div style={styles.container}>
        <TopNavigation />
        <div style={styles.main} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.headerCover}>
        <ImageWithFallback src={page.coverPhotoURL} alt="" style={styles.coverImage} />
      </div>
      <div style={styles.main}>
        <div style={styles.headerRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ImageWithFallback src={page.photoURL} alt="" style={styles.avatar} />
            <div>
              <h1 style={styles.title}>{page.name}{(page as any).verified && <span style={{ marginLeft: 6, verticalAlign: 'middle' }}><VerifiedBadge size={20} /></span>}</h1>
              <p style={styles.username}>@{page.username}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={handleFollowToggle} style={styles.primaryBtn}>
              {isFollowing ? 'Unfollow' : 'Follow'}
            </button>
            {!isOwner && page.ownerId && (
              <button onClick={() => navigate(`/messages?user=${encodeURIComponent(page.ownerId)}`)} style={styles.secondaryBtn}>
                💬 Message
              </button>
            )}
            {canManage && (
              <button onClick={() => setEditing(e => !e)} style={styles.secondaryBtn}>
                {editing ? 'Cancel' : 'Edit Info'}
              </button>
            )}
            {canManage && (
              <button onClick={() => navigate('/pages')} style={styles.secondaryBtn}>
                Manage Team
              </button>
            )}
            {canManage && (
              <button onClick={() => navigate(`/create-ad?pageId=${page.id}`)} style={styles.promoteBtn}>
                Promote Page
              </button>
            )}
          </div>
        </div>

        {!editing ? (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>About</h3>
            <p style={styles.text}><strong>Category:</strong> {page.category || 'N/A'}</p>
            <p style={styles.text}>{page.description}</p>
            <p style={{ ...styles.text, color: '#5B6E6E' }}>{page.followers?.length || 0} followers</p>
          </div>
        ) : (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Edit Info</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} style={styles.textarea} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSave} style={styles.primaryBtn}>Save</button>
              <button onClick={() => setEditing(false)} style={styles.secondaryBtn}>Cancel</button>
            </div>
          </div>
        )}

        {canManage && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Create Post as {page.name}</h3>
            <textarea
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              rows={3}
              placeholder="Share something..."
              style={styles.textarea}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                htmlFor="page-file"
                style={{
                  ...styles.secondaryBtn,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 280,
                  display: 'inline-block',
                }}
              >
                {uploadingMedia ? `Preparing... ${Math.round(compressionProgress)}%` : (mediaFile ? `📎 ${mediaFile.name}` : '📷 Add Photo/Video')}
              </label>
              <input id="page-file" ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFileSelect} style={{ display: 'none' }} />
              {mediaFile && !uploadingMedia && (
                <>
                  <button style={styles.secondaryBtn} onClick={() => fileRef.current?.click()}>Change</button>
                  <button style={styles.dangerBtn} onClick={handleRemoveSelectedMedia}>Remove</button>
                </>
              )}
              {moderating && <span style={{ fontSize: 12, color: '#5B6E6E' }}>Scanning…</span>}
              <button onClick={handleCreatePagePost} disabled={posting || (!postText.trim() && !mediaFile)} style={styles.primaryBtn}>
                {posting ? (moderating ? 'Posting… (Scanning…)': 'Posting...') : 'Post'}
              </button>
            </div>
            {mediaFile && !uploadingMedia && (
              <div style={{ paddingTop: 10 }}>
                <SelectedMediaThumb key={`${mediaFile.name}-${mediaFile.size}`} file={mediaFile} large onRemove={handleRemoveSelectedMedia} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', background: 'var(--color-background-alt)' },
  main: { maxWidth: 900, margin: '0 auto', padding: '20px', paddingBottom: '80px' },
  headerCover: { height: 220, background: '#ddd' },
  coverImage: { width: '100%', height: '100%', objectFit: 'cover' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -40 },
  avatar: { width: 96, height: 96, borderRadius: '50%', border: '4px solid #fff', objectFit: 'cover' },
  title: { margin: 0, fontSize: 24, color: 'var(--color-text)' },
  username: { margin: '4px 0 0', color: 'var(--color-text-muted)' },
  section: { background: 'var(--color-surface)', borderRadius: 12, padding: 16, marginTop: 16, border: '1px solid var(--color-border)' },
  sectionTitle: { margin: '0 0 12px', fontSize: 18, color: 'var(--color-text)' },
  text: { margin: '6px 0', color: 'var(--color-text)' },
  formGroup: { display: 'flex', flexDirection: 'column' as const, gap: 6, marginBottom: 12 },
  label: { color: '#5B6E6E', fontSize: 13 },
  input: { padding: '10px 12px', borderRadius: 8, border: '2px solid var(--color-border)', fontSize: 14 },
  textarea: { padding: '10px 12px', borderRadius: 8, border: '2px solid var(--color-border)', fontSize: 14, fontFamily: 'inherit' },
  primaryBtn: { padding: '10px 14px', background: 'var(--color-primary-600)', color: 'var(--color-surface)', border: 'none', borderRadius: 8, cursor: 'pointer' },
  secondaryBtn: { padding: '10px 14px', background: 'var(--color-border)', color: 'var(--color-text)', border: 'none', borderRadius: 8, cursor: 'pointer' },
  promoteBtn: { padding: '10px 14px', background: '#0B6B6F', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
};

export default PageDetail;






